import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import {
  ArrowLeft,
  PackageCheck,
  AlertTriangle,
  CheckCircle2,
  Receipt,
  Trash2,
  Pencil,
} from "lucide-react";
import { fetchWithAuth, SessionExpiredError } from "@/lib/auth-client";
import type { GRNDetailDto, GRNCondition, GRNListItemDto } from "@procureflow/shared-types";
import { canEditDraftGrn } from "@/lib/draft-edit-eligibility";
import { formatDate, formatDateTime } from "@/lib/format";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/auth-context";
import { Attachments } from "@/components/attachments";
import { SubmitForApprovalButton } from "@/components/submit-for-approval-button";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

export const Route = createFileRoute("/grn/$id")({
  head: () => ({
    meta: [{ title: "Goods Receipt Note — ProcureFlow" }],
  }),
  component: GRNDetailPage,
});

// ── Condition pill ─────────────────────────────────────────────────────────────
// US-031: visual indicator — yellow for Damaged, red for Rejected.

const CONDITION_STYLES: Record<GRNCondition, { label: string; className: string }> = {
  Good: {
    label: "Good",
    className: "bg-[var(--success)]/15 [color:var(--success)]",
  },
  Damaged: {
    label: "Damaged",
    className: "bg-[var(--warning)]/15 [color:var(--warning)]",
  },
  Rejected: {
    label: "Rejected",
    className: "bg-destructive/15 text-destructive",
  },
};

function ConditionPill({ condition }: { condition: GRNCondition }) {
  const style = CONDITION_STYLES[condition];
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium",
        style.className,
      )}
    >
      {style.label}
    </span>
  );
}

// ── Page component ──────────────────────────────────────────────────────────────

function GRNDetailPage() {
  const { id } = Route.useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [detail, setDetail] = useState<GRNDetailDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [errorStatus, setErrorStatus] = useState<number | null>(null);
  // #3: delete-draft confirmation Dialog state.
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // True when this page was reached straight from recording the receipt (US-030
  // success feedback). One-shot: the flag is set by the receive form and cleared
  // here so it shows once, not on every later visit.
  const [justRecorded, setJustRecorded] = useState(false);
  // US-066 — whether this GR still needs an invoice. The detail DTO carries no
  // has_invoice flag, so derive it from the uninvoiced GR list: a GR present in
  // ?uninvoiced=true has not yet been converted to an invoice.
  const [needsInvoice, setNeedsInvoice] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem("grn:justRecorded") === id) {
      sessionStorage.removeItem("grn:justRecorded");
      setJustRecorded(true);
    }
  }, [id]);

  useEffect(() => {
    let cancelled = false;

    async function fetchDetail() {
      if (import.meta.env.DEV) {
        const { mswReady } = await import("@/mocks/browser");
        await mswReady;
      }
      setLoading(true);
      setError(null);
      setErrorStatus(null);
      try {
        const res = await fetchWithAuth(`/api/grns/${id}`);
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          if (!cancelled) {
            setErrorStatus(res.status);
            setError(body.message ?? "Failed to load goods receipt note.");
          }
          return;
        }
        const { data } = await res.json();
        if (!cancelled) setDetail(data);
        // US-066 — determine whether this GR is still awaiting an invoice.
        try {
          const invRes = await fetchWithAuth("/api/grns?uninvoiced=true&limit=100");
          if (invRes.ok) {
            const { data: uninvoiced } = (await invRes.json()) as { data: GRNListItemDto[] };
            if (!cancelled) setNeedsInvoice(uninvoiced.some((g) => g.id === id));
          }
        } catch {
          // Non-critical — the action just stays hidden if this fails.
        }
      } catch (err) {
        if (err instanceof SessionExpiredError) return;
        if (!cancelled) setError("An unexpected error occurred.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchDetail();
    return () => {
      cancelled = true;
    };
  }, [id]);

  // #3: delete a draft (Recorded) GRN. DELETE releases the source PO(s)'
  // received quantity (server-side); on success we leave the now-deleted page.
  async function handleDelete() {
    if (!detail) return;
    setDeleting(true);
    try {
      const res = await fetchWithAuth(`/api/grns/${detail.id}`, { method: "DELETE" });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        toast.error(body?.message ?? "Failed to delete goods receipt note.");
        return;
      }
      setDeleteOpen(false);
      toast.success("Goods receipt note deleted — its purchase order has been released.");
      navigate({ to: "/grn" });
    } catch (err) {
      if (err instanceof SessionExpiredError) return;
      toast.error("An unexpected error occurred.");
    } finally {
      setDeleting(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <div className="h-6 w-6 rounded-full border-2 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }

  if (error || !detail) {
    return (
      <div className="space-y-4">
        <Link
          to="/grn"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Goods Receipt Notes
        </Link>
        <div className="rounded-2xl bg-card border border-border shadow-elegant p-10 text-center space-y-2">
          {errorStatus === 403 ? (
            <>
              <p className="text-sm font-medium text-foreground">Access denied</p>
              <p className="text-sm text-muted-foreground">
                You do not have permission to view this goods receipt note.
              </p>
            </>
          ) : errorStatus === 404 ? (
            <>
              <p className="text-sm font-medium text-foreground">Not found</p>
              <p className="text-sm text-muted-foreground">
                This goods receipt note does not exist or has been removed.
              </p>
            </>
          ) : (
            <p className="text-sm text-destructive">{error}</p>
          )}
        </div>
      </div>
    );
  }

  const overReceiptCount = detail.line_items.filter((li) => li.variance > 0).length;
  // #3: only a draft (Recorded) GRN may be deleted, and only by the roles that
  // record receipts (ProcurementOfficer / Admin). The BE enforces the boundary.
  const canDelete =
    detail.status === "Recorded" && (user?.role === "ProcurementOfficer" || user?.role === "Admin");
  // US-077: a draft (Recorded) GRN can be fully edited (header + lines, re-syncing the PO).
  const canEdit = canEditDraftGrn(detail.status, user?.role);

  return (
    <div className="space-y-5">
      {/* Back link */}
      <Link
        to="/grn"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Goods Receipt Notes
      </Link>

      {/* Success confirmation — shown once, straight after recording (US-030) */}
      {justRecorded && (
        <div className="flex items-start gap-2 rounded-lg border border-[var(--success)]/40 bg-[var(--success)]/10 px-4 py-3">
          <CheckCircle2 className="h-4 w-4 [color:var(--success)] mt-0.5 shrink-0" />
          <p className="text-sm [color:var(--success)]">
            Goods receipt {detail.grn_number} recorded successfully.
          </p>
        </div>
      )}

      {/* Header card */}
      <div className="rounded-2xl bg-card border border-border shadow-elegant p-6 space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="space-y-2 min-w-0">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-lg bg-[var(--success)]/10 grid place-items-center [color:var(--success)] shrink-0">
                <PackageCheck className="h-3.5 w-3.5" />
              </div>
              <span className="font-mono text-sm font-medium text-muted-foreground">
                {detail.grn_number}
              </span>
            </div>
            <h1 className="text-lg font-semibold tracking-tight leading-snug">
              Goods Receipt
              {detail.purchase_order ? ` — ${detail.purchase_order.vendor_name}` : ""}
            </h1>
            <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm text-muted-foreground">
              {/* US-065 — single-PO GRN links its one source PO; a multi-PO GRN
                  lists each distinct source PO from source_documents below. */}
              {detail.purchase_order ? (
                <span>
                  <span className="text-foreground font-medium">PO:</span>{" "}
                  <Link
                    to="/purchase-orders/$id"
                    params={{ id: detail.purchase_order.id }}
                    className="font-mono text-xs hover:text-primary transition-colors"
                  >
                    {detail.purchase_order.po_number}
                  </Link>
                </span>
              ) : detail.source_documents.length > 0 ? (
                <span className="inline-flex flex-wrap items-center gap-1.5">
                  <span className="text-foreground font-medium">Source POs:</span>
                  {detail.source_documents.map((doc, i) => (
                    <span key={doc.id} className="inline-flex items-center">
                      <Link
                        to="/purchase-orders/$id"
                        params={{ id: doc.id }}
                        className="font-mono text-xs hover:text-primary transition-colors"
                      >
                        {doc.number}
                      </Link>
                      {i < detail.source_documents.length - 1 ? "," : ""}
                    </span>
                  ))}
                </span>
              ) : null}
              <span>
                <span className="text-foreground font-medium">Received By:</span>{" "}
                {detail.receiver_name}
              </span>
              <span>
                <span className="text-foreground font-medium">Date Received:</span>{" "}
                {formatDate(detail.date_received)}
              </span>
              <span>
                <span className="text-foreground font-medium">Created By:</span>{" "}
                {detail.created_by_name}
              </span>
              <span>
                <span className="text-foreground font-medium">Created:</span>{" "}
                {formatDateTime(detail.created_at)}
              </span>
            </div>
          </div>
          <div className="shrink-0 text-right space-y-3">
            <div>
              <div className="text-2xl font-bold tabular-nums tracking-tight">
                {detail.line_items.length}
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">
                line{detail.line_items.length !== 1 ? "s" : ""} received
              </div>
            </div>
            {detail.status === "Recorded" &&
              (user?.role === "ProcurementOfficer" || user?.role === "Admin") && (
                <SubmitForApprovalButton
                  endpoint={`/api/grns/${detail.id}/submit`}
                  onSubmitted={(data) => setDetail(data as GRNDetailDto)}
                />
              )}
            {/* US-077 — edit this draft GRN (header + lines), re-syncing the source PO. */}
            {canEdit && (
              <Button size="sm" variant="outline" data-testid="edit-grn-button" asChild>
                <Link to="/grn/build" search={{ editId: detail.id }}>
                  <Pencil className="h-3.5 w-3.5" /> Edit
                </Link>
              </Button>
            )}
            {/* #3 — delete this draft GRN, releasing the receipt it booked on
                its source PO(s). Recorded + ProcurementOfficer/Admin only. */}
            {canDelete && (
              <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
                <DialogTrigger asChild>
                  <Button size="sm" variant="outline" data-testid="delete-grn-button">
                    <Trash2 className="h-3.5 w-3.5" /> Delete
                  </Button>
                </DialogTrigger>
                <DialogContent dismissable={false}>
                  <DialogHeader>
                    <DialogTitle>Delete this draft goods receipt note?</DialogTitle>
                    <DialogDescription>
                      This permanently deletes the goods receipt note and releases the quantity it
                      recorded against its source purchase order, returning that PO to its
                      receivable state. This can't be undone.
                    </DialogDescription>
                  </DialogHeader>
                  <DialogFooter>
                    <Button
                      variant="outline"
                      onClick={() => setDeleteOpen(false)}
                      disabled={deleting}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={handleDelete}
                      disabled={deleting}
                      data-testid="confirm-delete-grn"
                    >
                      {deleting ? "Deleting…" : "Delete GRN"}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
            {/* US-066 — convert this GR to an invoice (mandatory terminal hop).
                Shown for GRs not yet invoiced; gated to invoice-creating roles. */}
            {needsInvoice &&
              (user?.role === "Finance" ||
                user?.role === "ProcurementOfficer" ||
                user?.role === "Admin") && (
                <Link
                  to="/invoices/from-grns"
                  search={{ grId: detail.id, editId: "" }}
                  data-testid="convert-to-invoice-button"
                  className="inline-flex items-center gap-1.5 rounded-lg bg-gradient-primary px-3.5 py-1.5 text-sm font-medium text-primary-foreground shadow-glow"
                >
                  <Receipt className="h-3.5 w-3.5" />
                  Convert to Invoice
                </Link>
              )}
          </div>
        </div>

        {/* Delivery reference */}
        <div className="rounded-lg bg-muted/50 border border-border px-4 py-3">
          <p className="text-xs font-medium text-muted-foreground mb-1">Delivery Reference</p>
          <p className="text-sm text-foreground leading-relaxed">
            {detail.delivery_reference ?? "—"}
          </p>
        </div>

        {/* Over-receipt banner — US-033 */}
        {overReceiptCount > 0 && (
          <div className="flex items-start gap-2 rounded-lg border border-[var(--warning)]/40 bg-[var(--warning)]/10 px-4 py-3">
            <AlertTriangle className="h-4 w-4 [color:var(--warning)] mt-0.5 shrink-0" />
            <p className="text-sm [color:var(--warning)]">
              {overReceiptCount} line{overReceiptCount !== 1 ? "s have" : " has"} an over-receipt —
              cumulative quantity received exceeds the quantity ordered.
            </p>
          </div>
        )}
      </div>

      {/* Line items — quantity-shaped, built concretely (transaction-agent-brief §5) */}
      <div className="rounded-2xl bg-card border border-border shadow-elegant overflow-hidden">
        <div className="px-5 py-4 border-b border-border">
          <h2 className="text-sm font-semibold">Received Line Items</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs text-muted-foreground">
              <tr>
                <th className="text-left font-medium py-3 px-5">Description</th>
                {/* US-065 — per-line source PO is only meaningful when the GRN
                    aggregates several POs; a single-PO GRN already shows its one
                    PO in the header, so the column is hidden to avoid duplicate
                    PO links. */}
                {detail.source_documents.length > 1 && (
                  <th className="text-left font-medium py-3 px-3">Source PO</th>
                )}
                <th className="text-right font-medium py-3 px-3 tabular-nums">Ordered</th>
                <th className="text-right font-medium py-3 px-3 tabular-nums">Received</th>
                <th className="text-left font-medium py-3 px-3">Condition</th>
                <th className="text-right font-medium py-3 px-3 tabular-nums">Variance</th>
                <th className="text-left font-medium py-3 px-5">Notes</th>
              </tr>
            </thead>
            <tbody>
              {detail.line_items.map((li) => {
                const isOver = li.variance > 0;
                const isUnder = li.variance < 0;
                return (
                  <tr
                    key={li.id}
                    className={cn("border-t border-border", isOver && "bg-[var(--warning)]/5")}
                  >
                    <td className="py-3.5 px-5 font-medium">{li.po_line_description}</td>
                    {detail.source_documents.length > 1 && (
                      <td className="py-3.5 px-3">
                        {li.source_po_id ? (
                          <Link
                            to="/purchase-orders/$id"
                            params={{ id: li.source_po_id }}
                            className="font-mono text-xs text-muted-foreground hover:text-primary transition-colors"
                          >
                            {li.source_po_number ?? "—"}
                          </Link>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                    )}
                    <td className="py-3.5 px-3 text-right tabular-nums text-muted-foreground">
                      {li.ordered_qty}
                    </td>
                    <td className="py-3.5 px-3 text-right tabular-nums font-semibold">
                      {li.quantity_received}
                    </td>
                    <td className="py-3.5 px-3">
                      <ConditionPill condition={li.condition} />
                    </td>
                    <td
                      className={cn(
                        "py-3.5 px-3 text-right tabular-nums font-medium",
                        isOver && "[color:var(--warning)]",
                        isUnder && "text-muted-foreground",
                        !isOver && !isUnder && "[color:var(--success)]",
                      )}
                    >
                      <span className="inline-flex items-center gap-1">
                        {isOver && <AlertTriangle className="h-3.5 w-3.5" />}
                        {li.variance > 0 ? `+${li.variance}` : li.variance}
                      </span>
                    </td>
                    <td className="py-3.5 px-5 text-muted-foreground">{li.notes ?? "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="px-5 py-3 border-t border-border text-xs text-muted-foreground">
          Variance is cumulative Good-condition quantity received across all receipts for this PO
          line, minus the quantity ordered. Positive values indicate an over-receipt.
        </div>
      </div>
      <Attachments ownerType="grns" ownerId={id} />
    </div>
  );
}
